<div [attr.aria-busy]="isLoading()">
  <div class="page-head">
    <div class="min-w-0">
      <p class="eyebrow">ภาพรวมฝ่ายบัญชี</p>
      <h1>ภาพรวมบัญชี</h1>
      <p>สวัสดี, {{ auth.user()?.firstName }} · ติดตามเงินรับ เงินจ่าย และสถานะการโอนเงิน</p>
    </div>
    <app-refresh-button
      label="รีเฟรชข้อมูลบัญชี"
      [loading]="isLoading()"
      (refresh)="loadDashboard()"
    />
  </div>

  <section class="mb-7 flex justify-end" aria-label="ตัวกรองช่วงวันที่รายงาน">
    <div class="min-w-0 lg:ml-auto">
      <form
        class="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap"
        aria-label="ช่วงวันที่รายงาน"
        (ngSubmit)="applyFilters()"
        novalidate
      >
        <label for="accounting-date-from" class="block min-w-0 sm:w-44">
          <span class="mb-1.5 block text-xs font-medium text-slate-500">วันที่เริ่มต้น</span>
          <p-datepicker
            name="dateFrom"
            inputId="accounting-date-from"
            [ngModel]="pendingDateFrom()"
            (ngModelChange)="pendingDateFrom.set($event)"
            dateFormat="dd/mm/yy"
            placeholder="เลือกวันที่เริ่มต้น"
            size="small"
            iconDisplay="input"
            [showIcon]="true"
            [fluid]="true"
            [disabled]="isLoading()"
            appendTo="body"
          />
        </label>
        <label for="accounting-date-to" class="block min-w-0 sm:w-44">
          <span class="mb-1.5 block text-xs font-medium text-slate-500">วันที่สิ้นสุด</span>
          <p-datepicker
            name="dateTo"
            inputId="accounting-date-to"
            [ngModel]="pendingDateTo()"
            (ngModelChange)="pendingDateTo.set($event)"
            dateFormat="dd/mm/yy"
            placeholder="เลือกวันที่สิ้นสุด"
            size="small"
            iconDisplay="input"
            [showIcon]="true"
            [fluid]="true"
            [disabled]="isLoading()"
            appendTo="body"
          />
        </label>
        <div class="col-span-2 flex flex-wrap items-center justify-end gap-2">
          <button
            pButton
            type="submit"
            size="small"
            aria-label="ค้นหาข้อมูลตามช่วงวันที่"
            title="ค้นหาข้อมูล"
            [disabled]="isLoading()"
          >
            <lucide-icon pButtonIcon name="Search" [size]="14" />
          </button>
        </div>
      </form>
    </div>
  </section>

  @if (error()) {
    <div
      class="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      role="alert"
    >
      <lucide-icon name="CircleHelp" [size]="20" />
      <span class="min-w-0 flex-1">{{ error() }}</span>
      <button
        type="button"
        class="border-0 bg-transparent text-sm font-semibold underline"
        (click)="applyFilters()"
        [disabled]="isLoading()"
      >
        ลองอีกครั้ง
      </button>
    </div>
  }
  @if (isLoading()) {
    <p
      class="mb-5 rounded-xl bg-[var(--theme-soft)] px-4 py-3 text-sm text-[var(--theme-ink)]"
      role="status"
    >
      กำลังโหลดข้อมูลบัญชี…
    </p>
  }

  @if (dashboard()) {
    <section class="mb-7" aria-label="สรุปการเงิน">
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <article class="card p-5">
          <div class="flex items-start justify-between gap-3">
            <p class="m-0 text-xs font-medium text-slate-500">เงินรับ–จ่ายสุทธิ</p>
            <span class="stat-icon blue shrink-0"
              ><lucide-icon name="WalletCards" [size]="20"
            /></span>
          </div>
          <p
            class="mt-2 mb-0 text-2xl font-bold tabular-nums"
            [class.text-emerald-700]="netTotal() >= 0"
            [class.text-red-700]="netTotal() < 0"
          >
            {{ netTotal() | number: '1.2-2' }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">บาท · เงินรับหักเงินจ่าย</p>
        </article>
        <article class="card p-5">
          <div class="flex items-start justify-between gap-3">
            <p class="m-0 text-xs font-medium text-slate-500">รับชำระแล้ว</p>
            <span class="stat-icon green shrink-0"
              ><lucide-icon name="ArrowDownLeft" [size]="20"
            /></span>
          </div>
          <p class="mt-2 mb-0 text-2xl font-bold text-slate-800 tabular-nums">
            {{ incomeTotal() | number: '1.2-2' }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            บาท · ตรวจสอบแล้ว {{ incomeCount() | number }} รายการ
          </p>
        </article>
        <article class="card p-5">
          <div class="flex items-start justify-between gap-3">
            <p class="m-0 text-xs font-medium text-slate-500">จ่ายแล้ว</p>
            <span class="stat-icon shrink-0 bg-red-50 text-red-600"
              ><lucide-icon name="ArrowUpRight" [size]="20"
            /></span>
          </div>
          <p class="mt-2 mb-0 text-2xl font-bold text-slate-800 tabular-nums">
            {{ expenseTotal() | number: '1.2-2' }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            บาท · จ่ายสำเร็จ {{ expenseCount() | number }} รายการ
          </p>
        </article>
        <article class="card p-5">
          <div class="flex items-start justify-between gap-3">
            <p class="m-0 text-xs font-medium text-slate-500">รอโอนจ่าย · ทุกช่วงเวลา</p>
            <span class="stat-icon amber shrink-0"><lucide-icon name="Clock3" [size]="20" /></span>
          </div>
          <p class="mt-2 mb-0 text-2xl font-bold text-slate-800 tabular-nums">
            {{ transferTotal() | number: '1.2-2' }}
          </p>
          <p class="mt-1 mb-0 text-xs text-slate-500">
            บาท · อนุมัติแล้ว {{ transferCount() | number }} รายการ
          </p>
        </article>
      </div>
    </section>

    <section class="mb-5" aria-label="การเคลื่อนไหวทางการเงิน">
      <div class="grid gap-5 xl:grid-cols-3">
        <section class="card min-w-0 xl:col-span-2" aria-labelledby="cash-chart-title">
          <div class="card-head flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 id="cash-chart-title" class="font-bold">แนวโน้มเงินรับ–จ่าย</h2>
              <p>
                {{ monthlyChart() ? 'สรุปรายเดือน' : 'สรุปรายวัน' }} · เฉพาะรายการที่ดำเนินการแล้ว
              </p>
            </div>
            <div class="flex items-center gap-3 text-xs text-slate-500">
              <span class="flex items-center gap-1.5"
                ><i class="size-2 rounded-full" [style.background-color]="chartTheme()?.income"></i
                >เงินรับ</span
              >
              <span class="flex items-center gap-1.5"
                ><i class="size-2 rounded-full" [style.background-color]="chartTheme()?.expense"></i
                >เงินจ่าย</span
              >
            </div>
          </div>
          @if (transactionCount()) {
            <div class="h-72 p-5 sm:h-80">
              @defer (when chartTheme()) {
                <p-chart
                  type="bar"
                  [data]="barData()"
                  [options]="barOptions()"
                  height="100%"
                  [ariaLabel]="'เงินรับเทียบเงินจ่าย ' + rangeLabel()"
                />
              } @error {
                <p class="text-sm text-slate-500" role="status">
                  ไม่สามารถแสดงกราฟได้ กรุณาดูข้อมูลในตาราง
                </p>
              }
            </div>
          } @else {
            <div class="empty">
              <b><lucide-icon name="Banknote" [size]="24" /></b>
              <h3>ยังไม่มีรายการรับ–จ่าย</h3>
              <p>ลองเลือกช่วงวันที่อื่นเพื่อดูข้อมูล</p>
            </div>
          }
        </section>
        <section class="card min-w-0" aria-labelledby="expense-chart-title">
          <div class="card-head">
            <h2 id="expense-chart-title" class="font-bold">สัดส่วนเงินจ่าย</h2>
            <p>ยอดที่จ่ายจริงในช่วงวันที่เลือก</p>
          </div>
          @if (expenseTotal() > 0) {
            <div class="relative mx-auto mt-5 h-56 max-w-xs">
              @defer (when chartTheme()) {
                <p-chart
                  type="doughnut"
                  [data]="doughnutData()"
                  [options]="doughnutOptions()"
                  height="224px"
                  ariaLabel="สัดส่วนเงินจ่าย แยกค่าใช้จ่าย คอมมิชชั่น และเงินคืนลูกค้า"
                />
              } @error {
                <p class="text-sm text-slate-500" role="status">
                  ไม่สามารถแสดงกราฟได้ กรุณาดูยอดเงินด้านล่าง
                </p>
              }
              <div
                class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                aria-hidden="true"
              >
                <span class="text-xs text-slate-500">เงินจ่ายทั้งหมด</span>
                <strong class="my-1 text-2xl text-slate-800 tabular-nums">{{
                  expenseTotal() | number: '1.0-0'
                }}</strong>
                <small class="text-xs text-slate-500">บาท</small>
              </div>
            </div>
          } @else {
            <div class="empty">
              <b><lucide-icon name="ReceiptText" [size]="24" /></b>
              <p class="mt-3">ยังไม่มีรายการจ่ายในช่วงนี้</p>
            </div>
          }
          <ul class="m-0 list-none p-5">
            @for (item of expenseBreakdown(); track item.category) {
              <li class="flex items-center justify-between gap-3 py-2 text-xs">
                <span class="flex items-center gap-2 text-slate-600"
                  ><i class="size-2 rounded-full" [style.background-color]="item.color"></i
                  >{{ item.label }}</span
                >
                <span class="flex items-center gap-3 tabular-nums"
                  ><strong class="text-slate-800">{{ item.amount | number: '1.2-2' }}</strong
                  ><small class="min-w-9 text-right text-slate-500"
                    >{{ item.percentage | number: '1.0-1' }}%</small
                  ></span
                >
              </li>
            }
          </ul>
        </section>
      </div>
    </section>
  } @else if (!isLoading() && error()) {
    <div class="card empty">
      <b><lucide-icon name="CircleHelp" [size]="24" /></b>
      <h3>ยังไม่สามารถแสดงภาพรวมบัญชีได้</h3>
      <p>กดลองอีกครั้งเพื่อโหลดข้อมูล</p>
    </div>
  }
</div>
